// components/ModalAddNewPersonalData/ModalAddNewPersonalData.tsx
import "./style.scss";

import { FC, useContext, useState, useEffect } from "react";

import Modal from "shared/ui/Modal";
import { PersonalDataContext } from "features/personal-data";
import { PersonalData } from "features/personal-data";
import { useNavigate } from "react-router-dom";
import { encryptApiBodyFromStringWordPress } from "shared/api/cryptApiBody";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  personalData?: PersonalData; // Not null when it for update
  isForBreach?: boolean; // Si true, affiche le champ entreprise pour signaler une fuite
}

interface DataTypeOption {
  id: string;
  label: string;
  value: PersonalData['type'];
}

interface ErrorItem {
  item?: any;
  error: string;
  existingData?: any;
}

// Liste d'entreprises pour l'autocomplétion
const companySuggestions = [
  "Amazon",
  "Apple",
  "Facebook",
  "Google",
  "Microsoft",
  "Netflix",
  "Twitter",
  "LinkedIn",
  "Instagram",
  "WhatsApp",
  "Snapchat",
  "TikTok",
  "Uber",
  "Airbnb",
  "Spotify",
  "PayPal",
  "Adobe",
  "Salesforce",
  "Oracle",
  "IBM",
  "Samsung",
  "Sony",
  "Nintendo",
  "EA Sports",
  "Blizzard",
  "Ubisoft",
  "Epic Games",
  "Discord",
  "Reddit",
  "Pinterest",
  "Tumblr",
  "Flickr",
  "Dropbox",
  "Slack",
  "Zoom",
  "Telegram",
  "Signal",
  "Booking.com",
  "Expedia",
  "TripAdvisor",
  "Yelp",
  "DoorDash",
  "Postmates",
  "Instacart",
  "Walmart",
  "Target",
  "Best Buy",
  "Home Depot",
  "Lowes",
  "CVS",
  "Walgreens",
  "Starbucks",
  "McDonald's",
  "Coca-Cola",
  "Pepsi",
  "Nike",
  "Adidas",
  "Zara",
  "H&M",
  "LVMH",
  "Hermès",
  "Gucci",
  "Chanel",
  "Louis Vuitton",
  "Cartier",
  "Rolex",
  "Tesla",
  "SpaceX",
  "Boeing",
  "Airbus",
  "Delta",
  "American Airlines",
  "United Airlines",
  "British Airways",
  "Air France",
  "Lufthansa",
  "Emirates",
  "Qatar Airways",
  "Singapore Airlines",
  "BNP Paribas",
  "Société Générale",
  "Crédit Agricole",
  "LCL",
  "Caisse d'Épargne",
  "Banque Populaire",
  "La Banque Postale",
  "Orange",
  "SFR",
  "Bouygues Telecom",
  "Free",
  "Sosh",
  "Red by SFR",
  "EDF",
  "Engie",
  "TotalEnergies",
  "Auchan",
  "Carrefour",
  "Leclerc",
  "Intermarché",
  "Casino",
  "Lidl",
  "Aldi"
];

const dataTypeOptions: DataTypeOption[] = [
  { id: "phone", label: "Numéro de téléphone", value: "phoneNumber" },
  // { id: "iban", label: "IBAN", value: "iban" },
  // { id: "address", label: "Adresse postale", value: "address" },
  // { id: "social_security", label: "Numéro de sécurité sociale", value: "socialSecurityNumber" },
  // { id: "password", label: "Mot de passe", value: "password" },
  { id: "email", label: "Email", value: "email" },
];

// Map des types vers leurs labels
const typeLabels: Record<PersonalData['type'], string> = {
  email: "Email",
  phoneNumber: "Numéro de téléphone",
  iban: "IBAN",
  address: "Adresse postale",
  socialSecurityNumber: "Numéro de sécurité sociale",
  password: "Mot de passe",
};

// Map des types vers leurs placeholders
const typePlaceholders: Record<PersonalData['type'], string> = {
  email: "exemple@email.com",
  phoneNumber: "+33 6 XX XX XX XX",
  iban: "FR76 XXXX XXXXX XXXXX XXXXX XXXXX",
  address: "Saisissez votre adresse postale",
  socialSecurityNumber: "1 XX XX XX XXX XXX XX",
  password: "Saisissez votre mot de passe",
};

const ModalAddNewPersonalData: FC<ModalProps> = ({ isOpen, onClose, personalData, isForBreach = false }) => {
  const { addPersonalDatas, updatePersonalData } = useContext(PersonalDataContext);

  const navigate = useNavigate();

  const [selectedTypes, setSelectedTypes] = useState<PersonalData['type'][]>([]);
  const [formData, setFormData] = useState<Record<PersonalData['type'], string>>({
    email: "",
    phoneNumber: "",
    iban: "",
    address: "",
    socialSecurityNumber: "",
    password: "",
  });
  const [animatingField, setAnimatingField] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [companySuggestionsFiltered, setCompanySuggestionsFiltered] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // États pour les erreurs
  const [errors, setErrors] = useState<ErrorItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Déterminer si on est en mode signalement de fuite
  const isBreachMode = isForBreach || personalData?.isDataBreached === true;

  // Reset form when modal opens/closes or when editing
  useEffect(() => {
    if (isOpen) {
      // Réinitialiser les erreurs
      setErrors([]);
      setIsSubmitting(false);

      if (personalData) {
        // Populate form for editing (single item)
        setSelectedTypes([personalData.type]);
        setFormData({
          email: "",
          phoneNumber: "",
          iban: "",
          address: "",
          socialSecurityNumber: "",
          password: "",
          [personalData.type]: personalData.value,
        });
        // Si la donnée a déjà une fuite, remplir le nom de l'entreprise
        if (personalData.companyWhereDataBreached) {
          setCompanyName(personalData.companyWhereDataBreached);
        }
      } else {
        // Reset form for new entry
        setSelectedTypes([]);
        setFormData({
          email: "",
          phoneNumber: "",
          iban: "",
          address: "",
          socialSecurityNumber: "",
          password: "",
        });
        setCompanyName("");
      }
    }
  }, [isOpen, personalData]);

  // Filtrer les suggestions d'entreprises
  useEffect(() => {
    if (companyName.length > 1) {
      const filtered = companySuggestions.filter(company =>
        company.toLowerCase().includes(companyName.toLowerCase())
      );
      setCompanySuggestionsFiltered(filtered.slice(0, 8)); // Limiter à 8 suggestions
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  }, [companyName]);

  const handleTypeToggle = (typeValue: PersonalData['type']) => {
    // Réinitialiser les erreurs quand l'utilisateur modifie la sélection
    setErrors([]);

    setSelectedTypes(prev =>
      prev.includes(typeValue)
        ? prev.filter(t => t !== typeValue)
        : [...prev, typeValue]
    );

    // Animation sur le nouveau champ
    if (!selectedTypes.includes(typeValue)) {
      setAnimatingField(typeValue);
      setTimeout(() => setAnimatingField(null), 300);
    }
  };

  const handleValueChange = (type: PersonalData['type'], value: string) => {
    // Réinitialiser les erreurs quand l'utilisateur modifie une valeur
    setErrors([]);
    setFormData(prev => ({ ...prev, [type]: value }));
  };

  const handleCompanySelect = (company: string) => {
    setCompanyName(company);
    setShowSuggestions(false);
    // Réinitialiser les erreurs
    setErrors([]);
  };

  const handleConfirm = async () => {
    if (selectedTypes.length === 0) return;

    setIsSubmitting(true);
    setErrors([]);

    try {
      if (personalData) {
        // Update existing (single item)
        const updateData: any = {
          value: formData[selectedTypes[0]],
        };

        // Si en mode fuite, mettre à jour les champs de fuite
        if (isBreachMode) {
          updateData.isDataBreached = true;
          updateData.companyWhereDataBreached = companyName;
        }

        const response = await updatePersonalData(personalData.id!, updateData);

        if (response?.error) {
          // Afficher l'erreur dans le modal
          setErrors([{ error: response.error }]);
        } else if (response?.errors && response.errors.length > 0) {
          // Afficher les erreurs multiples
          setErrors(response.errors);
        } else {
          // Succès - fermer le modal
          onClose();
        }
      } else {
        // Add multiple new personal data entries
        const newDataList: any[] = selectedTypes
          .filter(type => formData[type]?.trim())
          .map(type => ({
            type,
            value: formData[type].trim(),
            // Si en mode fuite, ajouter les champs de fuite
            ...(isBreachMode && {
              isDataBreached: true,
              companyWhereDataBreached: companyName,
            })
          }));

        if (newDataList.length > 0) {
          const response = await addPersonalDatas(newDataList);

          if (response?.errors && response.errors.length > 0) {
            // Afficher les erreurs dans le modal
            setErrors(response.errors);
          } else if (response?.error) {
            setErrors([{ error: response.error }]);
          } else {
            // Succès - fermer le modal
            if (formData['email']?.trim()) {
              navigate(`/particuliers/fuites-donnees?email=${encryptApiBodyFromStringWordPress(formData['email']?.trim())}`);
            }
            onClose();
          }
        }
      }
    } catch (error: any) {
      // Gérer les erreurs inattendues
      setErrors([{ error: error.message || "Une erreur inattendue s'est produite" }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getInputType = (type: PersonalData['type']) => {
    if (type === 'email') return 'email';
    if (type === 'phoneNumber') return 'tel';
    if (type === 'password') return 'password';
    if (type === 'address') return 'address';
    return 'text';
  };

  const hasValidData = () => {
    if (selectedTypes.length === 0) return false;
    const hasValues = selectedTypes.some(type => formData[type]?.trim());

    // En mode fuite, le nom de l'entreprise est obligatoire
    if (isBreachMode && hasValues && !companyName.trim()) {
      return false;
    }

    return hasValues;
  };

  // Déterminer le titre et sous-titre
  const getTitle = () => {
    if (personalData) {
      return isBreachMode ? "Signaler une fuite" : "Modifier une donnée";
    }
    return isBreachMode ? "Signaler une fuite" : "Ajouter des données";
  };

  const getSubtitle = () => {
    if (personalData) {
      return isBreachMode ? "Indiquez l'entreprise concernée" : "Modifier la valeur";
    }
    return isBreachMode ? "Sélectionnez les données qui ont fuité" : "Ajouter des données personnelles";
  };

  const getConfirmLabel = () => {
    if (personalData) {
      return isBreachMode ? "Signaler" : "Modifier";
    }
    return isBreachMode ? "Signaler" : "Ajouter";
  };

  // @ts-ignore
  // @ts-ignore
  return (
    <>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          title={getTitle()}
          subtitle={getSubtitle()}
          type="info"
          labelOnConfirm={getConfirmLabel()}
          onConfirm={handleConfirm}
          confirmButtonDisabled={!hasValidData() || isSubmitting}
          bgBody={'bg-primary-70'}
        >
          <div className="ModalAddNewPersonalData">
            {/* Affichage des erreurs */}
            {errors.length > 0 && (
              <div className="error-messages">
                {errors.map((err, idx) => (
                  <div key={idx} className="error-item">
                    <div className="error-content">
                      <span className="error-text">{err.error}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="data-types-section">
              <span className="data-types-section__title">Type de données</span>
              <div className="data-types-section__options">
                {dataTypeOptions.map((option) => (
                  <label key={option.id} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(option.value)}
                      onChange={() => handleTypeToggle(option.value)}
                      className="checkbox-label__input"
                    />
                    <span className="checkbox-label__text">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {selectedTypes.length > 0 && (
              <div className="form-fields-section">
                <div className="form-fields-section__title">
                  Renseignez les données sélectionnées
                </div>
                <div className="form-fields-section__fields">
                  {selectedTypes.map((type) => (
                    <div
                      key={type}
                      className={`form-field ${animatingField === type ? 'form-field--animate' : ''}`}
                    >
                      <label className="form-field__label">
                        {typeLabels[type]}
                      </label>
                      <input
                        type={getInputType(type)}
                        name={getInputType(type)}
                        className="form-field__input"
                        placeholder={typePlaceholders[type]}
                        value={formData[type]}
                        onChange={(e) => handleValueChange(type, e.target.value)}
                        autoFocus={selectedTypes.length === 1}
                        disabled={isSubmitting}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Champ entreprise en mode fuite */}
            {isBreachMode && (
              <div className="company-field-section">
                <div className="company-field">
                  <label className="company-field__label">
                    Entreprise concernée <span className="required">*</span>
                  </label>
                  <div className="company-field__input-container">
                    <input
                      type="text"
                      className="company-field__input"
                      placeholder="Nom de l'entreprise où la donnée a fuité"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      onFocus={() => companyName.length > 1 && setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 1200)}
                      disabled={isSubmitting}
                    />
                    {showSuggestions && companySuggestionsFiltered.length > 0 && (
                      <div className="company-suggestions">
                        {companySuggestionsFiltered.map((company) => (
                          <div
                            key={company}
                            className="company-suggestion-item"
                            onClick={() => handleCompanySelect(company)}
                          >
                            {company}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="company-field__help">
                    Indiquez le nom de l'entreprise qui a subi la fuite de données
                  </p>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default ModalAddNewPersonalData;